import { Component, Input, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { UpdatePointCustomField } from 'src/app/project/modules/points/points.actions';
import { TPoint } from 'src/app/project/modules/points/points.model';

import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PointsUpdateService } from 'src/app/project/modules/points/points-update.service';
import { PointActivityService } from '../../point-timeline/point-activity.service';
import { PointFieldsService } from '../point-fields.service';

import { TPointUpdate } from '@project/view-models';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { getTimeAsUtcMidday } from '../../../../../../core/helpers/date/date';
import { EIconPath } from '../../../../../shared/enums/icons.enum';

@Component({
    selector: 'pp-point-fields-date',
    templateUrl: './point-fields-date.component.html',
    styleUrls: ['./point-fields-date.component.scss', '../point-fields.component.scss'],
    standalone: false
})
export class PointFieldsDateComponent implements OnDestroy {
  @Input() ppWorkspaceId: string;
  @Input() ppPointId: string;
  @Input() ppFieldId: string;
  @Input() ppFieldLabel: string;
  @Input() ppFieldValue: string | number;
  @Input() ppNew: boolean;
  @Input() ppCanEdit: boolean;
  @Input() ppFieldDescription: string;
  @Input() ppIsLocked: boolean;

  private readonly destroy$ = new Subject<void>();

  updating = false;
  error = false;
  focused = false;
  EIconPath = EIconPath;

  constructor(
    private store: Store<{ points: TPoint[] }>,
    private promptService: PromptService,
    private pointFieldsService: PointFieldsService,
    private pointActivityService: PointActivityService,
    private sitePointFilterService: SitePointFilterService,
    private pointsUpdateService: PointsUpdateService,
    private translationPipe: TranslationPipe,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
  }

  clearCustomField(): void {
    this.ppFieldValue = null;
    this.updateField();
  }

  updateDate(dates: Date[]): void {
    if (!dates) {
      this.clearCustomField();

      return;
    }

    this.ppFieldValue = getTimeAsUtcMidday(dates[0]);
    this.updateField();
  }

  updateField(): void {
    const _id = this.ppPointId;

    if (this.ppNew) {
      this.updateFieldForNewPoint(
        this.ppPointId,
        this.ppFieldValue,
        this.ppWorkspaceId,
        this.ppFieldId,
      );

      return;
    }
    this.updateExistingPoint(_id);
  }

  private updateExistingPoint(_id: string): void {
    const body: TPointUpdate = {
      customFieldsList: [
        {
          customFieldTemplateId: this.ppFieldId,
          value: this.ppFieldValue ? this.ppFieldValue.toString() : null,
        },
      ],
    };

    this.updating = true;

    this.pointsUpdateService
      .updatePointField(_id, body)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.handleUpdateSuccess(_id);
        }),
        catchError((error) => {
          this.pointFieldsService.showUpdatePointFieldError(error);

          this.error = true;

          return of();
        }),
        finalize(() => {
          this.updating = false;
        }),
      )
      .subscribe();
  }

  private handleUpdateSuccess(_id: string): void {
    const promptText = this.translationPipe.transform('prompt_point_date_update');

    this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
    this.promptService.showSuccess(promptText);
    this.pointActivityService.refreshTimeline(this.ppWorkspaceId, _id);
  }

  private updateFieldForNewPoint(
    pointId: string,
    fieldValue: string | number,
    workspaceId: string,
    customFieldId: string,
  ): void {
    this.store.dispatch(
      new UpdatePointCustomField({
        workspaceId: workspaceId,
        pointId: pointId,
        customFieldId: customFieldId,
        customFieldValue: fieldValue,
      }),
    );
  }
}
